// TopicChapterStrip — color-coded chapter bar that sits directly
// under a video player. Each segment is a colored block sized
// proportionally to its duration; hover shows a tooltip with
// label + start time, click seeks the video to the segment start.
//
// Why a separate strip instead of overlaying the native video
// scrubber: HTML5 <video controls> doesn't expose its scrub bar
// for custom rendering. Replacing controls means rebuilding
// keyboard shortcuts, fullscreen, captions, mobile playback —
// not worth the regression risk for an MVP. A strip directly
// under the player gives ~95% of the visual benefit and stays
// out of the player's way (Loom + Descript both ship this
// pattern for chapter markers).
//
// Reused by:
//   - CallsPhase CompletedDetail / MeetingDetail (call topics
//     from callSummary.topics)
//   - App.jsx demo trailer detail (demo moments from demo.moments,
//     adapted via { startSeconds, endSeconds, label })
//
// Empty / missing data: returns null so callers can mount it
// unconditionally without conditional jsx blocks.

import { useEffect, useState } from 'react';

// Distinct-but-harmonized palette. 8 colors so up to 8 topics
// each get a unique hue; topic 9+ wraps. Picked for readability
// against the dark-blue tooltip text + white video background.
const PALETTE = [
  '#0ea5e9', // sky
  '#8b5cf6', // violet
  '#10b981', // emerald
  '#f59e0b', // amber
  '#ec4899', // pink
  '#06b6d4', // cyan
  '#84cc16', // lime
  '#f97316', // orange
];

const fmtTime = (s) => {
  if (typeof s !== 'number' || !isFinite(s)) return '';
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${String(sec).padStart(2, '0')}`;
};

export default function TopicChapterStrip({
  segments,
  totalDurationSeconds,
  onSeek,
  // Optional: pass the video element ref to unlock two extras —
  //   (1) the segment containing currentTime gets a brighter
  //       active-state outline + opacity 1, so the user can see
  //       where they are without scrubbing
  //   (2) keyboard shortcuts: '[' jumps to previous chapter,
  //       ']' jumps to next chapter (only fires when focus is
  //       NOT inside an input / textarea — defensive against
  //       stealing keystrokes from comment composers etc.)
  videoRef = null,
  // Optional UI tuning
  height = 28,
  showLegend = true,
}) {
  const [hoverIdx, setHoverIdx] = useState(null);
  const [activeIdx, setActiveIdx] = useState(null); // segment containing currentTime

  // Normalize: clamp segments to [0, total], drop zero-width.
  // Computed BEFORE any early return so the useEffects below stay
  // unconditionally-called (rules-of-hooks). Returns an empty array
  // for invalid inputs — the early-return after the effects handles
  // bailing cleanly.
  const safeTotal = (totalDurationSeconds && totalDurationSeconds > 0) ? totalDurationSeconds : 0;
  const cleaned = (Array.isArray(segments) && safeTotal > 0)
    ? segments
        .map((s, i) => {
          const start = Math.max(0, Math.min(safeTotal, Number(s.startSeconds) || 0));
          const end = Math.max(start, Math.min(safeTotal, Number(s.endSeconds) || 0));
          return {
            idx: i,
            start,
            end,
            width: ((end - start) / safeTotal) * 100,
            left: (start / safeTotal) * 100,
            label: s.label || `Topic ${i + 1}`,
            color: s.color || PALETTE[i % PALETTE.length],
          };
        })
        .filter((s) => s.width > 0.1) // drop sub-0.1% slivers (noise)
    : [];

  // Subscribe to the video's timeupdate so we can highlight the
  // currently-playing segment. Bail cleanly if no videoRef passed
  // (component still works, just without active-state UI).
  // ESLint warning ignored intentionally: cleaned is rebuilt each
  // render but the effect closure only reads .start/.end which are
  // structurally stable for a given segments+duration prop.
  useEffect(() => {
    const v = videoRef?.current;
    if (!v) return undefined;
    const onTime = () => {
      const t = v.currentTime || 0;
      const hit = cleaned.find((s) => t >= s.start && t < s.end);
      setActiveIdx(hit ? hit.idx : null);
    };
    v.addEventListener('timeupdate', onTime);
    onTime(); // sync on mount in case the user is mid-playback
    return () => v.removeEventListener('timeupdate', onTime);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoRef, segments, totalDurationSeconds]);

  // Keyboard shortcuts: '[' = previous chapter, ']' = next chapter.
  // Skipped when focus is in an editable element so we don't
  // hijack typing in the Comments composer / Ask Winter input.
  useEffect(() => {
    if (!videoRef?.current || !onSeek || cleaned.length === 0) return undefined;
    const onKey = (e) => {
      if (e.key !== '[' && e.key !== ']') return;
      const tag = (document.activeElement?.tagName || '').toLowerCase();
      const editable = tag === 'input' || tag === 'textarea'
        || document.activeElement?.isContentEditable;
      if (editable) return;
      const t = videoRef.current.currentTime || 0;
      // For prev: find the latest segment that starts BEFORE
      // current time minus a small grace window (so spamming '['
      // walks chapters instead of replaying the current one).
      // For next: first segment that starts AFTER current time.
      const sorted = [...cleaned].sort((a, b) => a.start - b.start);
      let target = null;
      if (e.key === '[') {
        const prev = [...sorted].reverse().find((s) => s.start < t - 0.75);
        target = prev || sorted[0]; // wrap-clamp to start
      } else {
        const next = sorted.find((s) => s.start > t + 0.25);
        target = next || sorted[sorted.length - 1]; // clamp to last
      }
      if (target) {
        e.preventDefault();
        onSeek(target.start);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoRef, onSeek, segments, totalDurationSeconds]);

  // Defensive guards moved AFTER the hooks so rules-of-hooks holds.
  // Anything that would render an empty/broken strip just returns
  // null — the caller mounts unconditionally.
  if (cleaned.length === 0) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {/* The strip itself — absolute-positioned blocks over a track,
          which lets us handle gaps cleanly (segments don't have to
          tile perfectly, e.g. dead air between topics). */}
      <div
        role="group"
        aria-label="Topic chapters — click to jump to a moment"
        style={{
          position: 'relative',
          width: '100%',
          height,
          background: 'var(--room-border, #e2e8f0)',
          borderRadius: 6,
          overflow: 'visible', // tooltip needs to escape
        }}
      >
        {cleaned.map((s) => {
          const isHover = hoverIdx === s.idx;
          const isActive = activeIdx === s.idx;
          // Active state takes precedence over hover for opacity
          // (active = "you're here right now", deserves the brightest
          // treatment) but hover still bumps scale so the user gets
          // tactile feedback on whatever they're pointing at.
          const opacity = isActive ? 1 : (isHover ? 1 : 0.7);
          return (
            <button
              key={s.idx}
              type="button"
              onClick={() => onSeek && onSeek(s.start)}
              onMouseEnter={() => setHoverIdx(s.idx)}
              onMouseLeave={() => setHoverIdx(null)}
              onFocus={() => setHoverIdx(s.idx)}
              onBlur={() => setHoverIdx(null)}
              title={`${s.label} · ${fmtTime(s.start)}`}
              aria-label={`Jump to ${s.label} at ${fmtTime(s.start)}`}
              aria-current={isActive ? 'true' : undefined}
              style={{
                position: 'absolute',
                left: `${s.left}%`,
                width: `${s.width}%`,
                top: 0,
                bottom: 0,
                padding: 0,
                border: 'none',
                background: s.color,
                opacity,
                cursor: onSeek ? 'pointer' : 'default',
                transition: 'opacity 120ms ease, transform 120ms ease, box-shadow 180ms ease',
                transform: isActive ? 'scaleY(1.25)' : (isHover ? 'scaleY(1.15)' : 'scaleY(1)'),
                transformOrigin: 'center',
                // Hairline divider between adjacent segments so two
                // similarly-colored blocks (palette wraps at 8) still
                // read as separate. The active segment also gets a
                // bright outer ring so it's findable at a glance.
                boxShadow: isActive
                  ? `inset -1px 0 0 rgba(255, 255, 255, 0.5), 0 0 0 2px #ffffff, 0 0 0 3px ${s.color}`
                  : 'inset -1px 0 0 rgba(255, 255, 255, 0.5)',
                // First/last get rounded outer corners to match the track.
                borderTopLeftRadius: s.idx === 0 ? 6 : 0,
                borderBottomLeftRadius: s.idx === 0 ? 6 : 0,
                borderTopRightRadius: s.idx === cleaned.length - 1 ? 6 : 0,
                borderBottomRightRadius: s.idx === cleaned.length - 1 ? 6 : 0,
              }}
            />
          );
        })}

        {/* Hover tooltip — renders above the strip so it doesn't
            cover the strip itself. Position follows the hovered
            segment's left edge, clamped so the tooltip never
            spills past the right edge of the track. */}
        {hoverIdx != null && cleaned[hoverIdx] && (
          <Tooltip
            label={cleaned.find((s) => s.idx === hoverIdx)?.label}
            time={fmtTime(cleaned.find((s) => s.idx === hoverIdx)?.start)}
            // Center over the segment, but clamp 5%–95% so the
            // tooltip stays inside the strip.
            leftPct={Math.max(5, Math.min(95,
              cleaned.find((s) => s.idx === hoverIdx).left +
              cleaned.find((s) => s.idx === hoverIdx).width / 2
            ))}
          />
        )}
      </div>

      {/* Legend — short labeled chips below the strip, in the same
          color order. Helps the AE map color → topic without having
          to hover every block. Hidden via showLegend=false for
          callers that already render their own list (e.g. the demo
          "In This Trailer" section). */}
      {showLegend && (
        <div style={{
          display: 'flex', flexWrap: 'wrap', gap: 6,
          fontSize: 11, color: 'var(--room-text-muted)',
        }}>
          {cleaned.map((s) => (
            <button
              key={`leg-${s.idx}`}
              type="button"
              onClick={() => onSeek && onSeek(s.start)}
              onMouseEnter={() => setHoverIdx(s.idx)}
              onMouseLeave={() => setHoverIdx(null)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '4px 8px',
                border: '1px solid var(--room-border, #e2e8f0)',
                borderRadius: 999,
                background: hoverIdx === s.idx ? 'var(--room-surface, #f8fafc)' : 'white',
                cursor: onSeek ? 'pointer' : 'default',
                fontFamily: 'inherit', fontSize: 11,
                color: 'var(--room-text-body)',
                transition: 'background 120ms ease, border-color 120ms ease',
              }}
            >
              <span style={{
                width: 8, height: 8, borderRadius: 2,
                background: s.color, flexShrink: 0,
              }} />
              <span style={{
                maxWidth: 160, overflow: 'hidden',
                textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {s.label}
              </span>
              <span style={{
                fontVariantNumeric: 'tabular-nums',
                color: 'var(--room-text-subtle)', fontWeight: 600,
              }}>
                {fmtTime(s.start)}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Tooltip({ label, time, leftPct }) {
  return (
    <div
      style={{
        position: 'absolute',
        left: `${leftPct}%`,
        bottom: '100%',
        transform: 'translateX(-50%)',
        marginBottom: 8,
        padding: '6px 10px',
        background: 'var(--room-text-strong, #0f172a)',
        color: 'white',
        borderRadius: 6,
        fontSize: 12,
        fontWeight: 500,
        whiteSpace: 'nowrap',
        pointerEvents: 'none',
        boxShadow: '0 4px 12px rgba(15, 23, 42, 0.25)',
        zIndex: 5,
      }}
    >
      <span>{label}</span>
      <span style={{
        marginLeft: 8,
        opacity: 0.7,
        fontVariantNumeric: 'tabular-nums',
      }}>
        {time}
      </span>
      {/* Caret pointing down to the strip. */}
      <span style={{
        position: 'absolute',
        left: '50%', top: '100%',
        transform: 'translateX(-50%)',
        width: 0, height: 0,
        borderLeft: '5px solid transparent',
        borderRight: '5px solid transparent',
        borderTop: '5px solid #0f172a',
      }} />
    </div>
  );
}
